import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MapPin, Loader2, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTOCOMPLETE = `${BACKEND_URL}/api/places/autocomplete`;
const DETAILS = `${BACKEND_URL}/api/places/details`;
const DEBOUNCE_MS = 280;

/**
 * AddressAutocomplete — a single street-address input with predictive suggestions.
 *
 * Props:
 *   value         - current street string
 *   onChange      - called as user types (street only)
 *   onSelect      - called with full {street, city, state, state_code, county, zip}
 *                   when a suggestion is picked
 *   label, required, testId, placeholder, disabled
 *
 * UX:
 *   - Debounced server fetch (~280ms after stop typing)
 *   - Loading spinner inside the input
 *   - Dropdown with arrow-key navigation, Enter to pick, Escape to close
 *   - Click-outside dismisses
 *   - Touch-screen friendly tap targets
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  label,
  required,
  testId,
  placeholder = 'Start typing an address…',
  disabled,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [justPicked, setJustPicked] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch when value changes
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const r = await axios.get(AUTOCOMPLETE, { params: { q }, signal: ctrl.signal });
      setSuggestions(r.data || []);
      setHighlight(-1);
    } catch (e) {
      if (!axios.isCancel(e) && e.name !== 'CanceledError') {
        // network/proxy error — leave existing suggestions, just stop spinner
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (justPicked) return;        // don't re-query right after picking
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, justPicked, fetchSuggestions]);

  // Click-outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = async (s) => {
    setOpen(false);
    setSelecting(true);
    setJustPicked(true);
    onChange(s.street);   // optimistic — fill the street input now
    try {
      const r = await axios.get(DETAILS, { params: { id: s.id } });
      // Merge suggestion + details. Suggestion wins for `street` (it already
      // includes the house number). Details fills in county and may upgrade
      // city/state/zip when present.
      const merged = {
        street: s.street || r.data.street || '',
        unit: r.data.unit || '',
        city: r.data.city || s.city || '',
        state: r.data.state || s.state || '',
        state_code: r.data.state_code || s.state || '',
        county: r.data.county || '',
        zip: r.data.zip || s.zip || '',
      };
      onChange(merged.street);
      onSelect?.(merged);
    } catch (e) {
      // fall back to whatever was on the suggestion
      onSelect?.({
        street: s.street, city: s.city, state_code: s.state, state: s.state,
        county: '', zip: s.zip,
      });
    } finally {
      setSelecting(false);
      // Allow re-querying after a beat (in case user keeps editing)
      setTimeout(() => setJustPicked(false), 600);
    }
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && (suggestions.length > 0 || loading) && !selecting;

  return (
    <div ref={wrapRef} className="relative">
      {label && (
        <label className="text-[12.5px] font-semibold text-neutral-700 flex items-center gap-1">
          {label}{required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="mt-1.5 relative">
        <input
          ref={inputRef}
          data-testid={testId}
          value={value || ''}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled || selecting}
          autoComplete="off"
          className={cn(
            'w-full h-11 pl-3 pr-9 rounded-md bg-white border text-[14px] font-semibold text-neutral-900 placeholder:font-normal placeholder:text-neutral-400',
            'focus:outline-none focus:border-neutral-900 transition',
            disabled || selecting ? 'opacity-60 cursor-not-allowed' : '',
            'hairline'
          )}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {(loading || selecting) ? (
            <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
          ) : null}
        </div>
      </div>

      {showDropdown && (
        <div className="absolute z-30 left-0 right-0 mt-1.5 rounded-lg border hairline bg-white shadow-lg overflow-hidden rise-in" style={{ animationDuration: '180ms' }}>
          {loading && suggestions.length === 0 ? (
            <div className="px-3.5 py-3 flex items-center gap-2 text-[12px] text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Looking up addresses…
            </div>
          ) : suggestions.length === 0 ? (
            <div className="px-3.5 py-3 text-[12px] text-neutral-500">
              Keep typing — we&apos;ll find it.
            </div>
          ) : (
            <ul role="listbox" data-testid={`${testId}-suggestions`}>
              {suggestions.map((s, idx) => {
                const active = idx === highlight;
                return (
                  <li
                    key={s.id}
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                    className={cn(
                      'px-3.5 py-2.5 cursor-pointer transition flex items-start gap-2.5 border-b last:border-b-0 hairline-soft',
                      active ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-50'
                    )}
                  >
                    <MapPin className={cn('h-3.5 w-3.5 mt-0.5 shrink-0',
                      active ? 'text-[hsl(var(--kf-gold))]' : 'text-neutral-400')} />
                    <div className="min-w-0 flex-1">
                      <div className={cn('text-[13.5px] font-bold truncate',
                        active ? 'text-white' : 'text-neutral-900')}>
                        {s.street || s.label.split(',')[0]}
                      </div>
                      <div className={cn('text-[11.5px] truncate mono mt-0.5',
                        active ? 'text-white/70' : 'text-neutral-500')}>
                        {[s.city, s.state, s.zip].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    {active && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold))] shrink-0 mt-1" />}
                  </li>
                );
              })}
              <li className="px-3.5 py-1.5 text-[9px] uppercase tracking-wider text-neutral-400 font-semibold bg-neutral-50/60 text-right">
                ↑↓ navigate · ↵ select · esc close
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
